import styles from "../../styles/opsConsole.module.css";
import ui from "./reviewChrome.module.css";
import lr from "./ListingReview.module.css";

/**
 * LISTING — the whole vendor listing, read-only, in editor order
 * ===============================================================
 * The approvals screen only ever showed pricing, but the vendor editor has
 * eight sections, and the reviewer needs to see the listing a price would
 * sit on. This renders all eight.
 *
 * On a LIVE listing the vendor's content edits (basics, setting up, support,
 * images, legal) are staged, not published: they wait for this approval.
 * When `hasPendingContent`, the staged values (pendingProduct) are the main
 * content, each differing field shows what is "Live now", and sections carry
 * a "Pending — not live yet" badge. Admins only view content — wrong content
 * goes back to the vendor through Request changes.
 *
 * Without staged content, "Changed" markers compare the last APPROVED
 * submission's content snapshot (contentBefore) with this submission's
 * snapshot (contentAtSubmit), falling back to the live product when this
 * submission predates snapshots.
 */

const FULFILMENT = {
  manual: "Set up by hand once the order arrives",
  keys: "Licence keys from a pool",
  invite: "Invite emailed to the buyer's admin",
  api: "Provisioned automatically (API)",
};

const parse = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v !== "string") return v;
  try {
    return JSON.parse(v);
  } catch (e) {
    return null;
  }
};

const shots = (v) => {
  let list = v;
  if (typeof list === "string") {
    const p = parse(list);
    list = p === null ? (list.trim() ? [list.trim()] : []) : p;
  }
  if (!Array.isArray(list)) list = list ? [list] : [];
  return list
    .map((s) => (typeof s === "string" ? s : s?.url || s?.src || s?.link || ""))
    .map((s) => String(s || "").trim())
    .filter(Boolean);
};

const isHttp = (v) => /^https?:\/\/\S+$/i.test(String(v || "").trim());
const blank = (v) => v === null || v === undefined || String(v).trim() === "";
const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const norm = (v) => (blank(v) ? null : typeof v === "string" ? v.trim() : v);

/** One comparable shape for a snapshot or the live product. */
const fromSnapshot = (c) => {
  if (!c) return null;
  return {
    name: c.name,
    info: c.info,
    logoLink: c.logoLink,
    screenshot: shots(c.screenshot),
    metaData: parse(c.metaData) || {},
    sub_category_ids: Array.isArray(c.sub_category_ids) ? c.sub_category_ids : null,
    // Snapshots taken before tags were reviewed have no type2 — leave it
    // undefined so they never read as a tag change.
    tags: Array.isArray(c.type2) ? c.type2 : undefined,
  };
};
const fromProduct = (p) =>
  p && {
    name: p.name,
    info: p.description,
    logoLink: p.logo,
    screenshot: p.screenshots || [],
    metaData: parse(p.metaData) || {},
    sub_category_ids: (p.categories || []).map((c) => c.sub_category_id).filter(Boolean),
    tags: Array.isArray(p.tags) ? p.tags : undefined,
  };

const Badge = ({ kind, children }) => (
  <span className={`${styles.badge} ${ui.badge} ${styles[kind] || ""}`}>
    <i className={styles.dot} />
    {children}
  </span>
);

const NotProvided = () => <span className={lr.empty}>Not provided</span>;

const Card = ({ n, title, badge, children }) => (
  <div className={`${styles.card} ${ui.card}`}>
    <h3>
      <span className={lr.sectionNo}>{n}</span>
      {title}
      {badge}
    </h3>
    {children}
  </div>
);

export default function ListingReview({
  product,
  planType = [],
  planCount = 0,
  intervalCount = 0,
  featureCount = 0,
  contentBefore = null,
  contentAtSubmit = null,
  pendingProduct = null,
  hasPendingContent = false,
  onOpenPricing,
}) {
  if (!product) {
    return (
      <div className={`${styles.card} ${ui.card}`}>
        <p className={styles.note}>Listing content is not available for this submission.</p>
      </div>
    );
  }

  const pending = Boolean(hasPendingContent && pendingProduct);
  // Pending mode: live is the baseline, the staged content is what's shown.
  const liveProduct = product;
  const before = pending ? fromProduct(liveProduct) : fromSnapshot(contentBefore);
  const after = pending
    ? fromProduct(pendingProduct)
    : fromSnapshot(contentAtSubmit) || fromProduct(product);
  if (pending) product = pendingProduct;
  const get = (obj, path) => path.reduce((o, k) => (o == null ? undefined : o[k]), obj);

  /** Diff marker for one field: null when unchanged or nothing to compare. */
  const diff = (path, fmt = (v) => String(v)) => {
    if (!before || !after) return null;
    // Tags: only comparable when both sides carry them (older snapshots don't).
    if (path[0] === "tags" && (get(before, path) === undefined || get(after, path) === undefined)) {
      return null;
    }
    const a = norm(get(before, path));
    const b = norm(get(after, path));
    if (same(a, b)) return null;
    const shown = a === null ? "not provided" : fmt(a);
    return { was: shown };
  };
  const wasLabel = pending ? "Live now" : "was";

  const Val = ({ value, path, fmt, children }) => {
    const d = path ? diff(path, fmt) : null;
    return (
      <dd title={d ? `${pending ? "Live now" : "Was"}: ${d.was}` : undefined}>
        {children || (blank(value) ? <NotProvided /> : value)}
        {d && (
          <>
            {" "}
            <Badge kind={pending ? "warn" : "info"}>{pending ? "Pending" : "Changed"}</Badge>
            <span className={lr.was}>
              {wasLabel}: {d.was}
            </span>
          </>
        )}
      </dd>
    );
  };

  const changedBadge = (paths) =>
    paths.some((p) => diff(p)) ? (
      pending ? (
        <Badge kind="warn">Pending — not live yet</Badge>
      ) : (
        <Badge kind="info">Changed</Badge>
      )
    ) : null;

  // Live category names for "Live now", so the reviewer isn't handed raw ids.
  const liveCategoryLabel = (ids) => {
    if (!Array.isArray(ids) || !ids.length) return "not provided";
    if (!pending) return `${ids.length} categor${ids.length === 1 ? "y" : "ies"} (ids ${ids.join(", ")})`;
    const byId = new Map(
      (liveProduct.categories || []).map((c) => [
        c.sub_category_id,
        [c.category_name, c.sub_category_name].filter(Boolean).join(" › "),
      ]),
    );
    return ids.map((id) => byId.get(id) || `#${id}`).join(", ");
  };

  const meta = parse(product.metaData) || {};
  const fulfil = meta.fulfilment || {};
  const support = meta.support || {};
  const legal = meta.legal || {};
  const cats = product.categories || [];
  const tags = Array.isArray(product.tags) ? product.tags : [];
  // Tags approving this would add to the central master list (server-computed).
  const newTagSlugs = new Set(
    (pending && Array.isArray(pendingProduct.newTags) ? pendingProduct.newTags : []).map((t) =>
      String(t).toLowerCase(),
    ),
  );
  const screenshots = product.screenshots || [];

  const ext = (url) =>
    blank(url) ? (
      <NotProvided />
    ) : isHttp(url) ? (
      <a className={lr.extLink} href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    ) : (
      <>
        <span className={lr.prose}>{url}</span>
        <span className={lr.warnText}>Not a public http(s) URL</span>
      </>
    );

  const buyerTypes = (planType || []).filter(Boolean);

  return (
    <>
      <div className={`${styles.card} ${ui.card} ${ui.callout} ${styles.noteBox} ${ui.noteBox}`}>
        <b>
          {pending
            ? "Content changes are waiting for this approval"
            : "Content changes on a live listing wait for approval"}
        </b>
        <p>
          On a live listing, edits to basics, setting up, support, images and legal
          are held until this submission is approved, alongside pricing, features
          and buyer details. Content is view-only here — if something is wrong, use
          Request changes and the vendor will fix and resubmit.
          {pending
            ? " Pending marks show what buyers see live now."
            : before
              ? " No content is staged; Changed marks compare against the last approved submission."
              : " No content is staged and no earlier approved snapshot exists, so nothing is marked as changed."}
        </p>
      </div>

      <Card n={1} title="Basics" badge={changedBadge([["name"], ["info"], ["sub_category_ids"], ["tags"]])}>
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Name</dt>
          <Val value={product.name} path={["name"]} />
          <dt>Description</dt>
          <Val path={["info"]}>
            {blank(product.description) ? (
              <NotProvided />
            ) : (
              <span className={lr.prose}>{product.description}</span>
            )}
          </Val>
          <dt>Categories</dt>
          <Val
            path={["sub_category_ids"]}
            fmt={(v) => (Array.isArray(v) ? liveCategoryLabel(v) : String(v))}
          >
            {cats.length === 0 ? (
              <NotProvided />
            ) : (
              <span className={lr.chips}>
                {cats.map((c, i) => (
                  <span
                    key={`${c.category_id}-${c.sub_category_id}-${i}`}
                    className={`${lr.chip} ${c.is_primary ? lr.chipPrimary : ""}`}
                  >
                    {[c.category_name, c.sub_category_name].filter(Boolean).join(" › ") ||
                      `#${c.sub_category_id || c.category_id}`}
                    {c.is_primary && <span className={lr.chipTag}>Primary</span>}
                  </span>
                ))}
              </span>
            )}
          </Val>
          <dt>Tags</dt>
          <Val
            path={["tags"]}
            fmt={(v) => (Array.isArray(v) ? (v.length ? v.join(", ") : "none") : String(v))}
          >
            {tags.length === 0 ? (
              <NotProvided />
            ) : (
              <span className={lr.chips}>
                {tags.map((t) => (
                  <span
                    key={t}
                    className={`${lr.chip} ${newTagSlugs.has(String(t).toLowerCase()) ? lr.chipPrimary : ""}`}
                  >
                    {t}
                    {newTagSlugs.has(String(t).toLowerCase()) && (
                      <span className={lr.chipTag}>new tag</span>
                    )}
                  </span>
                ))}
              </span>
            )}
          </Val>
        </dl>
      </Card>

      <Card n={2} title="Plans & pricing">
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Plan types</dt>
          <dd>
            {buyerTypes.length ? buyerTypes.map((t) => t.name || "Untitled").join(", ") : <NotProvided />}
          </dd>
          <dt>Plans</dt>
          <dd>{planCount}</dd>
          <dt>Billing periods</dt>
          <dd>
            {intervalCount}{" "}
            {onOpenPricing && (
              <button type="button" className={lr.linkBtn} onClick={onOpenPricing}>
                Open full pricing
              </button>
            )}
          </dd>
        </dl>
      </Card>

      <Card n={3} title="Features">
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Feature groups</dt>
          <dd>
            {featureCount}{" "}
            {onOpenPricing && (
              <button type="button" className={lr.linkBtn} onClick={onOpenPricing}>
                See feature table
              </button>
            )}
          </dd>
        </dl>
      </Card>

      <Card n={4} title="Buyer details">
        <div className={lr.body}>
          {buyerTypes.length === 0 ? (
            <NotProvided />
          ) : (
            buyerTypes.map((t, ti) => {
              const fields = Array.isArray(t.required_fields) ? t.required_fields : parse(t.required_fields) || [];
              return (
                <div key={t.id || ti}>
                  <div className={lr.typeHead}>{t.name || "Untitled plan type"}</div>
                  {fields.length === 0 ? (
                    <p>
                      <NotProvided />
                    </p>
                  ) : (
                    <div className={lr.tableWrap}>
                      <table className={`${styles.table} ${ui.table}`}>
                        <thead>
                          <tr>
                            <th>Title</th>
                            <th>Name</th>
                            <th>Type</th>
                            <th>Required</th>
                            <th>Placeholder</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fields.map((f, i) => (
                            <tr key={`${f?.name}-${i}`}>
                              <td className={ui.cellStrong}>{f?.title || <NotProvided />}</td>
                              <td className={styles.mono}>{f?.name || "—"}</td>
                              <td>{f?.type || "text"}</td>
                              <td>{f?.required ? "Yes" : "No"}</td>
                              <td className={ui.cellQuiet}>{blank(f?.placeholder) ? "—" : f.placeholder}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Card>

      <Card
        n={5}
        title="Setting up"
        badge={changedBadge([
          ["metaData", "fulfilment", "method"],
          ["metaData", "fulfilment", "instructions"],
          ["metaData", "fulfilment", "sla_hours"],
        ])}
      >
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Method</dt>
          <Val
            value={fulfil.method ? FULFILMENT[fulfil.method] || fulfil.method : ""}
            path={["metaData", "fulfilment", "method"]}
            fmt={(v) => FULFILMENT[v] || v}
          />
          <dt>Instructions</dt>
          <Val path={["metaData", "fulfilment", "instructions"]}>
            {blank(fulfil.instructions) ? <NotProvided /> : <span className={lr.prose}>{fulfil.instructions}</span>}
          </Val>
          <dt>SLA</dt>
          <Val
            value={blank(fulfil.sla_hours) ? "" : `${fulfil.sla_hours} hours`}
            path={["metaData", "fulfilment", "sla_hours"]}
            fmt={(v) => `${v} hours`}
          />
        </dl>
      </Card>

      <Card
        n={6}
        title="Support"
        badge={changedBadge([
          ["metaData", "support", "email"],
          ["metaData", "support", "phone"],
          ["metaData", "support", "hours"],
          ["metaData", "support", "docs_url"],
        ])}
      >
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Email</dt>
          <Val value={support.email} path={["metaData", "support", "email"]} />
          <dt>Phone</dt>
          <Val value={support.phone} path={["metaData", "support", "phone"]} />
          <dt>Hours</dt>
          <Val value={support.hours} path={["metaData", "support", "hours"]} />
          <dt>Docs</dt>
          <Val path={["metaData", "support", "docs_url"]}>{ext(support.docs_url)}</Val>
        </dl>
      </Card>

      <Card n={7} title="Logo & images" badge={changedBadge([["logoLink"], ["screenshot"]])}>
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Logo</dt>
          <Val path={["logoLink"]} fmt={() => (pending ? "the previous logo" : "a different logo")}>
            {blank(product.logo) ? (
              <NotProvided />
            ) : (
              <a href={product.logo} target="_blank" rel="noopener noreferrer">
                <img className={lr.logo} src={product.logo} alt={`${product.name || "Product"} logo`} />
              </a>
            )}
          </Val>
          <dt>Screenshots</dt>
          <Val
            path={["screenshot"]}
            fmt={(v) => `${(v || []).length} image${(v || []).length === 1 ? "" : "s"}`}
          >
            {screenshots.length === 0 ? (
              <NotProvided />
            ) : (
              <span className={lr.thumbs}>
                {screenshots.map((src, i) => (
                  <a
                    key={`${src}-${i}`}
                    className={lr.thumbLink}
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open full image"
                  >
                    <img className={lr.thumb} src={src} alt={`Screenshot ${i + 1}`} loading="lazy" />
                  </a>
                ))}
              </span>
            )}
          </Val>
        </dl>
      </Card>

      <Card
        n={8}
        title="Legal"
        badge={changedBadge([
          ["metaData", "legal", "terms_url"],
          ["metaData", "legal", "privacy_url"],
          ["metaData", "legal", "eula_url"],
        ])}
      >
        <dl className={`${styles.kv} ${lr.kv}`}>
          <dt>Terms</dt>
          <Val path={["metaData", "legal", "terms_url"]}>
            {blank(legal.terms_url) ? (
              <>
                <NotProvided />
                <span className={lr.warnText}>Required</span>
              </>
            ) : (
              ext(legal.terms_url)
            )}
          </Val>
          <dt>Privacy</dt>
          <Val path={["metaData", "legal", "privacy_url"]}>{ext(legal.privacy_url)}</Val>
          <dt>EULA</dt>
          <Val path={["metaData", "legal", "eula_url"]}>{ext(legal.eula_url)}</Val>
        </dl>
      </Card>
    </>
  );
}
